import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import {
  BookingBloc,
  Breadcrumb,
  GiftcardItem,
  GiftcardSummary,
  HeroBanner,
  Icon,
  ProductShowSkeleton,
  SanitizedHtml,
  Skeleton,
  Slide,
  Slider,
  SocialShare,
  StrapiImg
} from 'components';
import { PAGE_TYPES } from 'constants';
import { useFetch } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

import defaultArrivajImg from 'assets/img/default-arrivaj-img.png';
import defaultTouloisirsImg from 'assets/img/default-touloisirs-img.png';
import 'assets/styles/components/products/product-show.scss';

const GiftcardShowPage = () => {
  const { id: giftcardId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const { call: giftcardCall, data: giftcardData, loading: giftcardLoading } = useFetch();
  const { call: similarGiftcardsCall, data: similarGiftcards, loading: similarGiftcardLoading } = useFetch();
  const { call: heroCall, data: heroResponse, loading: heroLoading } = useFetch({ filterUserCompany: true });

  const giftcard = useMemo(() => giftcardData?.data?.[0], [giftcardData]);
  const heroBanner = useMemo(() => heroResponse?.data?.[0], [heroResponse]);

  const breadcrumbs = useMemo(() => {
    return [
      { url: routes.giftcardHomePage.path, label: t('breadcrumb.home') },
      {
        url: `${routes.results.path}?type=${PAGE_TYPES.giftcards}`,
        label: t('breadcrumb.giftcards')
      },
      { url: '', label: giftcard?.title }
    ];
  }, [giftcard, location.pathname]);

  const giftcardPicture = useMemo(() => {
    if (!giftcard?.picture) {
      return {
        frontUrl: appService.isTouloisirs() ? defaultTouloisirsImg : defaultArrivajImg
      };
    }

    return giftcard.picture;
  }, [giftcard]);

  const features = useMemo(() => {
    if (isEmpty(giftcard)) return [];

    return [
      {
        title: t('products:giftcards.validity'),
        value: t('products:giftcards.validityDuration', { count: giftcard.validityMonths ?? 0 })
      },
      {
        title: t('products:giftcards.availableInInstallments'),
        value: giftcard.availableInInstallments ? t('yes') : t('no')
      },
      {
        title: t('products:giftcards.cancellationPolicy'),
        value: t('products:giftcards.nonCancelleable')
      },
      {
        title: t('products:giftcards.country'),
        value: 'France'
      },
      {
        title: t('products:giftcards.shipping'),
        value: t('products:giftcards.shippingDetails')
      },
      {
        title: t('products:giftcards.availableOnline'),
        value: giftcard.availableOnline ? t('yes') : t('no')
      },
      {
        title: t('products:giftcards.availableInStore'),
        value: giftcard.availableInStore ? t('yes') : t('no')
      }
    ];
  }, [giftcard]);

  const sliderOptions = useMemo(
    () => ({
      perPage: 1,
      mediaQuery: 'min',
      breakpoints: {
        2200: {
          perPage: 4
        },
        1920: {
          perPage: 3
        },
        640: {
          perPage: 2
        }
      },
      padding: { right: '20%' },
      perMove: 1,
      gap: '1.5rem'
    }),
    []
  );

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    const getHero = () => {
      heroCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/result-pages',
        query: {
          filters: {
            page_type: { name: { $eq: PAGE_TYPES.giftcards } }
          }
        }
      });
    };

    getHero();
  }, []);

  useEffect(() => {
    const getGiftcard = () => {
      giftcardCall({
        service: appService.getStrapiServiceName(),
        path: '/api/giftcards',
        method: 'get',
        query: {
          filters: {
            gift_card_id: { $eq: giftcardId }
          }
        }
      });
    };

    getGiftcard();
  }, [giftcardId]);

  useEffect(() => {
    const getSimilarGiftcards = () => {
      if (isEmpty(giftcard?.category)) return;

      similarGiftcardsCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/giftcards',
        query: {
          filters: {
            category: {
              slug: { $eq: giftcard.category.slug }
            },
            // Exclude the current giftcard from results
            gift_card_id: { $ne: giftcardId }
          }
        }
      });
    };

    getSimilarGiftcards();
  }, [giftcard]);

  return (
    <div className="product-show relative">
      <div className="hidden lg:block">
        <HeroBanner
          title={giftcard?.title || heroBanner?.heroTitle || ''}
          image={heroBanner?.heroImageDesktop}
          mobileImage={heroBanner?.heroImageMobile}
          loading={heroLoading}
        />
      </div>

      <div className="hidden main-container lg:block lg:mb-4 lg:mt-20">
        <Breadcrumb breadcrumbItems={breadcrumbs} loading={giftcardLoading} />
      </div>

      {giftcardLoading && <ProductShowSkeleton />}

      {!giftcardLoading && giftcard && (
        <>
          <div className="relative h-72 mt-16 md:h-96 lg:hidden print:hidden">
            <StrapiImg image={giftcardPicture} width="116" height="200" className="w-full h-full object-cover" />
          </div>

          <div className="product-container main-container flex flex-col lg:flex-row lg:justify-between">
            <h2 className="h1 underlined-blue mb-8 lg:mb-0">{giftcard.title}</h2>

            <div className="flex items-center flex-wrap gap-4 md:gap-8 lg:hidden">
              {giftcard?.availableInStore && (
                <div className="flex items-center gap-2">
                  <Icon name="bag-shopping" iconStyle="fad" classes="w-6 h-6 text-yellow-500" />
                  <span>{t('products:giftcards.useInStore')}</span>
                </div>
              )}

              {giftcard?.availableOnline && (
                <div className="flex items-center gap-2">
                  <Icon name="laptop" iconStyle="fad" classes="w-6 h-6 text-yellow-500" />
                  <span>{t('products:giftcards.useOnline')}</span>
                </div>
              )}
            </div>

            <div className="gap-4 hidden lg:flex">
              <SocialShare />

              <button type="button" onClick={() => window.print()}>
                <Icon name="print" iconStyle="fad" classes="w-4 h-4 text-gray-500 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>

          <div className="main-container mb-12 lg:gap-x-8 lg:flex lg:justify-between print:flex-col print:gap-x-6">
            <div className="w-full min-w-0">
              <SanitizedHtml content={giftcard.description} className="mb-6" />

              <h3 className="h2 underlined-yellow mt-12 mb-6">{t('products:giftcards.howToUse')}</h3>
              <SanitizedHtml content={giftcard.howToUse} />

              <h3 className="h2 underlined-yellow mt-12 mb-6">{t('products:giftcards.features')}</h3>
              {features.map((feature, index) => (
                <div
                  key={`feature-${index}`}
                  className={clsx(
                    'grid grid-cols-12 gap-x-4 border-t border-l border-r border-gray-200 p-2',
                    index + 1 === features.length && 'border-b'
                  )}
                >
                  <p className="col-span-6 md:col-span-4 font-bold text-gray-600">{feature.title}</p>
                  <p className="col-span-6 md:col-span-8">{feature.value}</p>
                </div>
              ))}

              {similarGiftcardLoading && (
                <div className="my-12">
                  <Skeleton classNames="w-1/3 h-12" />

                  <div className="flex gap-2">
                    {[...Array(4)].map((_, i) => (
                      <Skeleton type="square" key={`similar-giftcard-skeleton-${i}`} classNames="h-64 w-64" />
                    ))}
                  </div>
                </div>
              )}

              {!similarGiftcardLoading && similarGiftcards?.data?.length > 0 && (
                <div className="print:hidden">
                  <h3 className="h2 underlined-yellow my-12">{t('products:giftcards.otherProducts')}</h3>

                  <Slider type="slide" options={sliderOptions} sliderClass="with-card-shadow absolute-arrows top-right">
                    {similarGiftcards?.data?.map((giftcard, i) => (
                      <Slide key={`slider-giftcard-${i}`}>
                        <GiftcardItem giftcard={giftcard} />
                      </Slide>
                    ))}
                  </Slider>
                </div>
              )}
            </div>

            <div className="booking-container shrink-0">
              <GiftcardSummary giftcard={giftcard} />

              <BookingBloc blocks={giftcard?.reservationBlocs} bookingBlockLoading={giftcardLoading} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GiftcardShowPage;
